/* Admin · Shipping (P0 · Feb 2026)
 *
 * Single console for every shipment: filterable list + 5 problem-shipment
 * flag buckets (including external trackers) + carrier-invoice
 * reconciliation + buyer-pasted external tracker visibility + per-shipment
 * override actions (force-rebook, mark-delivered, flag-investigation).
 *
 * 4 tabs:
 *   • All shipments    — filterable table over `shipments` (54+ rows)
 *   • External trackers — buyer-pasted 3rd-party tracking numbers
 *   • Flags            — stuck / late / exception / flagged / external_stuck buckets
 *   • Carrier recon    — overcharge / undercharge / zero-cost detector
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  Truck, Search, RefreshCw, AlertTriangle, ScanLine, Flag, Loader2,
  Package, MapPin, Clock, DollarSign, ChevronRight, CheckCircle2, XCircle,
  ArrowLeftRight, PackageX, Pin, RotateCw, Activity, Globe, Wallet,
  TrendingUp, Settings,
} from "lucide-react";


function money(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
}
function fmtAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ago = (Date.now() - d.getTime()) / 1000;
    if (ago < 60) return "just now";
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    if (ago < 604800) return `${Math.floor(ago / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

const STATUS_COLORS = {
  delivered:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_transit:       "bg-sky-50 text-sky-700 border-sky-200",
  booked:           "bg-violet-50 text-violet-700 border-violet-200",
  label_created:    "bg-slate-50 text-slate-700 border-slate-200",
  pickup_scheduled: "bg-amber-50 text-amber-800 border-amber-200",
  exception:        "bg-rose-50 text-rose-700 border-rose-200",
  failed:           "bg-rose-50 text-rose-700 border-rose-200",
  returned:         "bg-rose-50 text-rose-700 border-rose-200",
};

const TABS = [
  { id: "list",     label: "All shipments",          icon: Package },
  { id: "external", label: "External trackers",      icon: Globe },
  { id: "flags",    label: "Flags",                  icon: Flag },
  { id: "recon",    label: "Carrier reconciliation", icon: ScanLine },
  { id: "wallet",   label: "Wallet & Markup",        icon: Wallet },
];


export default function AdminShipping() {
  const [tab, setTab] = useState("list");
  return (
    <div data-testid="admin-shipping" className="space-y-6">
      <PageHeader
        title="Shipping Console"
        subtitle="Every shipment · stuck/late flags · carrier-invoice reconciliation · force-rebook + mark-delivered overrides."
      />

      <div data-testid="ship-tabs" className="flex items-center gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`ship-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon size={15}/>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "list"     && <ListTab/>}
      {tab === "external" && <ExternalTab/>}
      {tab === "flags"    && <FlagsTab/>}
      {tab === "recon"    && <ReconTab/>}
      {tab === "wallet"   && <WalletTab/>}
    </div>
  );
}


/* ─── List tab ──────────────────────────────────────────────────────── */
function ListTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [carrier, setCarrier] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(90);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        since_days: String(sinceDays),
        limit: "100",
      });
      if (status)  params.set("status", status);
      if (carrier) params.set("carrier", carrier);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/shipping/list?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load shipments");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, carrier, sinceDays]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ship-kpis">
        <KpiTile label="Total"        value={data?.kpis?.total_count ?? "—"} icon={Package}/>
        <KpiTile label="Carrier cost" value={data ? money(data.kpis.total_cost) : "—"} icon={DollarSign}/>
        <KpiTile label="Buyer billed" value={data ? money(data.kpis.total_billed) : "—"} icon={DollarSign}/>
        <KpiTile label="Markup"       value={data ? money(data.kpis.markup_usd) : "—"} icon={DollarSign} tone="emerald"/>
        <KpiTile label="Distinct carriers" value={Object.keys(data?.by_carrier || {}).length} icon={Truck}/>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl p-3" data-testid="ship-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400"/>
          <Input
            data-testid="ship-filter-q"
            placeholder="Tracking, origin, destination …"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="ship-filter-status">
            <SelectValue placeholder="Any status"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="in_transit">In transit</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="exception">Exception</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(Number(v))}>
          <SelectTrigger className="w-[120px] h-9 text-sm" data-testid="ship-filter-since">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30d</SelectItem>
            <SelectItem value="90">90d</SelectItem>
            <SelectItem value="180">6mo</SelectItem>
            <SelectItem value="365">1yr</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="ship-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading shipments…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="ship-empty">No shipments match your filters.</div>
        ) : (
          <table className="w-full text-sm" data-testid="ship-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tracking</th>
                <th className="text-left px-4 py-2 font-medium">Carrier</th>
                <th className="text-left px-4 py-2 font-medium">Route</th>
                <th className="text-left px-4 py-2 font-medium">Order</th>
                <th className="text-right px-4 py-2 font-medium">Cost / Billed</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Age</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id}
                    data-testid={`ship-row-${r.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(r)}>
                  <td className="px-4 py-2 mono text-[11.5px] text-slate-700">{r.tracking || "—"}</td>
                  <td className="px-4 py-2 text-slate-700">{r.carrier || "—"}</td>
                  <td className="px-4 py-2 text-[12px] text-slate-600 max-w-[260px] truncate">
                    {r.origin || "—"} → {r.destination || "—"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">{r.order_code || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate max-w-[170px]">{r.seller_name || r.seller_email}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-[12px]">
                    <div>{money(r.cost)}</div>
                    <div className="text-slate-500">{money(r.billed)}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.days_open ?? "—"}d</td>
                  <td className="px-2 py-2 text-slate-400"><ChevronRight size={14}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <ShipmentDrawer shipment={selected} onClose={() => setSelected(null)} onRefresh={load}/>}
    </div>
  );
}


/* ─── Shipment detail drawer w/ override actions ───────────────────── */
function ShipmentDrawer({ shipment, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);   // "rebook" | "delivered" | "flag"

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/shipping/${shipment.id}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Could not load detail");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [shipment.id]);

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ship-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={16}/> Shipment · {(data?.shipment?.tracking || shipment.tracking || shipment.id?.slice(0,8))}
            </DialogTitle>
            <DialogDescription className="text-[12px] text-slate-500">
              Full life of this shipment + override actions.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2"/> Loading…</div>
          ) : data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3" data-testid="ship-summary">
                <Field label="Carrier" value={data.shipment.carrier}/>
                <Field label="Status"  value={data.shipment.status}/>
                <Field label="Provider" value={data.shipment.provider || "manual"}/>
                <Field label="Mode"    value={data.shipment.mode || "—"}/>
                <Field label="Origin"  value={data.shipment.origin}/>
                <Field label="Destination" value={data.shipment.destination}/>
                <Field label="Cost"    value={money(data.shipment.cost)}/>
                <Field label="Billed"  value={money(data.shipment.billed)}/>
                {data.shipment.tracking_url && (
                  <div className="col-span-2">
                    <a href={data.shipment.tracking_url} target="_blank" rel="noreferrer"
                       className="text-sky-700 text-[12px] underline" data-testid="ship-tracking-url">
                      Live tracking link →
                    </a>
                  </div>
                )}
              </div>

              {data.order && (
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-[12px] text-sky-900" data-testid="ship-order">
                  <div className="font-semibold">Order {data.order.code}</div>
                  <div>Total: {money(data.order.total)} · Status: {data.order.status}</div>
                  <div>Seller: {data.seller?.email || data.order.seller_name} · Buyer: {data.buyer?.email || data.order.buyer_name}</div>
                </div>
              )}

              {data.shipment.rebooked_at && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[12px] text-amber-900" data-testid="ship-rebooked-banner">
                  <div className="font-semibold">Force-rebooked {fmtAt(data.shipment.rebooked_at)}</div>
                  <div className="mt-1">Previously: <span className="mono">{data.shipment.previous_carrier} · {data.shipment.previous_tracking}</span></div>
                </div>
              )}

              {data.shipment.admin_flagged && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-[12px] text-rose-900" data-testid="ship-flagged-banner">
                  <div className="font-semibold flex items-center gap-1"><Flag size={13}/> Flagged for investigation</div>
                  <div className="mt-1">Reason: {data.shipment.admin_flag_reason}</div>
                </div>
              )}

              {data.history && data.history.length > 0 && (
                <div data-testid="ship-history">
                  <div className="text-[12px] font-semibold text-slate-700 mb-1.5">Override history</div>
                  <div className="space-y-1">
                    {data.history.map((h, i) => (
                      <div key={i} className="text-[11.5px] border border-slate-100 rounded-lg p-2 bg-slate-50">
                        <div className="font-mono text-slate-700">{h.action}</div>
                        <div className="text-slate-500">{fmtAt(h.at)} · {h.reason?.slice(0, 80)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-wrap gap-2">
            {data?.shipment?.status !== "delivered" && (
              <Button data-testid="ship-rebook-btn" variant="outline" onClick={() => setActionDialog("rebook")}>
                <RotateCw size={14} className="mr-1.5"/> Force-rebook
              </Button>
            )}
            {data?.shipment?.status !== "delivered" && (
              <Button data-testid="ship-delivered-btn" variant="outline" onClick={() => setActionDialog("delivered")}>
                <CheckCircle2 size={14} className="mr-1.5"/> Mark delivered
              </Button>
            )}
            <Button data-testid="ship-flag-btn" variant="outline" onClick={() => setActionDialog("flag")}>
              <Flag size={14} className="mr-1.5"/> {data?.shipment?.admin_flagged ? "Unflag" : "Flag for review"}
            </Button>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {actionDialog && (
        <ActionDialog
          kind={actionDialog}
          shipment={data?.shipment || shipment}
          onClose={() => setActionDialog(null)}
          onDone={() => { onRefresh && onRefresh(); onClose(); }}
        />
      )}
    </>
  );
}


function ActionDialog({ kind, shipment, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [newCarrier, setNewCarrier] = useState("");
  const [newTracking, setNewTracking] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = reason.trim().length >= 3 && (kind !== "rebook" || newCarrier.trim().length >= 2);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      let path, body;
      if (kind === "rebook") {
        path = `/admin/shipping/${shipment.id}/force-rebook`;
        body = { new_carrier: newCarrier, new_tracking: newTracking || undefined, reason };
      } else if (kind === "delivered") {
        path = `/admin/shipping/${shipment.id}/mark-delivered`;
        body = { reason };
      } else {
        path = `/admin/shipping/${shipment.id}/flag-investigation`;
        body = { reason };
      }
      await api.post(path, body);
      toast.success(
        kind === "rebook" ? `Rebooked with ${newCarrier}`
        : kind === "delivered" ? "Marked delivered"
        : "Flag updated"
      );
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const TITLES = {
    rebook:    "Force-rebook shipment",
    delivered: "Mark shipment delivered",
    flag:      "Toggle investigation flag",
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={`ship-action-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{TITLES[kind]}</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            {kind === "rebook" && "Replace the carrier + tracking number. Both fields are required."}
            {kind === "delivered" && "Manual override · use when carrier scan is missing but receipt is confirmed."}
            {kind === "flag" && "Pin or unpin this shipment from the admin review queue."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {kind === "rebook" && (
            <>
              <Input
                data-testid="ship-new-carrier"
                placeholder="New carrier (e.g. FedEx Ground, UPS Freight)"
                value={newCarrier}
                onChange={(e) => setNewCarrier(e.target.value)}
              />
              <Input
                data-testid="ship-new-tracking"
                placeholder="New tracking number (optional · auto-generated if blank)"
                value={newTracking}
                onChange={(e) => setNewTracking(e.target.value)}
              />
            </>
          )}
          <Textarea
            data-testid="ship-action-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for this override (≥3 chars)"
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            data-testid="ship-action-submit"
            onClick={submit}
            disabled={!canSubmit || busy}
          >
            {busy ? <Loader2 className="animate-spin mr-1.5" size={14}/> : null}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Flags tab ─────────────────────────────────────────────────────── */
function FlagsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("stuck");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/shipping/flags?since_days=180");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load flags");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.[bucket] || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="flag-kpis">
        <FlagTile label="Stuck (>14d)"   value={data?.counts?.stuck     ?? 0} active={bucket==="stuck"}     onClick={() => setBucket("stuck")}     icon={Clock}     tone="amber"  testid="flag-bucket-stuck"/>
        <FlagTile label="Late (>21d)"    value={data?.counts?.late      ?? 0} active={bucket==="late"}      onClick={() => setBucket("late")}      icon={AlertTriangle} tone="rose" testid="flag-bucket-late"/>
        <FlagTile label="Exceptions"     value={data?.counts?.exception ?? 0} active={bucket==="exception"} onClick={() => setBucket("exception")} icon={PackageX}  tone="rose"   testid="flag-bucket-exception"/>
        <FlagTile label="Manually flagged" value={data?.counts?.flagged ?? 0} active={bucket==="flagged"}   onClick={() => setBucket("flagged")}   icon={Pin}       tone="violet" testid="flag-bucket-flagged"/>
        <FlagTile label="External stuck (>7d)" value={data?.counts?.external_stuck ?? 0} active={bucket==="external_stuck"} onClick={() => setBucket("external_stuck")} icon={Globe} tone="amber" testid="flag-bucket-external-stuck"/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="flag-empty">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2"/>
            No {bucket} shipments. All clear.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid={`flag-table-${bucket}`}>
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tracking</th>
                <th className="text-left px-4 py-2 font-medium">Carrier</th>
                <th className="text-left px-4 py-2 font-medium">Route</th>
                {bucket === "external_stuck" && (
                  <th className="text-left px-4 py-2 font-medium">Buyer</th>
                )}
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Days open</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`flag-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 mono text-[11.5px]">{r.tracking || r.id?.slice(0, 8)}</td>
                  <td className="px-4 py-2">{r.carrier || "—"}</td>
                  <td className="px-4 py-2 text-[12px] text-slate-600 max-w-[280px] truncate">
                    {r.origin} → {r.destination}
                  </td>
                  {bucket === "external_stuck" && (
                    <td className="px-4 py-2 text-[12px] text-slate-700">
                      <div className="font-medium">{r.buyer_company || "—"}</div>
                      <div className="text-[11px] text-slate-500">{r.buyer_email || "—"}</div>
                    </td>
                  )}
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600 font-medium">{r.days_open}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/* ─── Carrier reconciliation tab ───────────────────────────────────── */
function ReconTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("overcharge");
  const [sinceDays, setSinceDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/shipping/carrier-recon?since_days=${sinceDays}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reconciliation");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays]);

  const rows = data?.buckets?.[bucket] || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recon-kpis">
        <FlagTile label="Overcharge"  value={data?.counts?.overcharge ?? 0} active={bucket==="overcharge"}  onClick={() => setBucket("overcharge")}  icon={ArrowLeftRight} tone="emerald" testid="recon-bucket-overcharge"/>
        <FlagTile label="Undercharge" value={data?.counts?.undercharge ?? 0} active={bucket==="undercharge"} onClick={() => setBucket("undercharge")} icon={XCircle} tone="rose"   testid="recon-bucket-undercharge"/>
        <FlagTile label="Zero cost"   value={data?.counts?.zero_cost ?? 0}  active={bucket==="zero_cost"}    onClick={() => setBucket("zero_cost")}   icon={AlertTriangle} tone="amber" testid="recon-bucket-zero-cost"/>
        <KpiTile label="Scanned"      value={data?.counts?.scanned ?? "—"}                                                                              icon={ScanLine}/>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(Number(v))}>
          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="recon-window">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 1 year</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="recon-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Re-scan
        </Button>
        <div className="ml-auto text-[11px] text-slate-500">
          Target markup: {data?.target_markup_pct ? `${(data.target_markup_pct * 100).toFixed(0)}%` : "—"} · Scanned {data?.scanned_at ? fmtAt(data.scanned_at) : "—"}
        </div>
      </div>

      {/* By-carrier volume strip */}
      {data && data.by_carrier && (
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="recon-by-carrier">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Volume by carrier ({sinceDays}d)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.by_carrier.map((c) => (
              <div key={c.carrier} className="rounded-lg border border-slate-100 p-2 text-[12px]" data-testid={`recon-carrier-${c.carrier?.replace(/\s/g,"_")}`}>
                <div className="font-semibold text-slate-800">{c.carrier}</div>
                <div className="text-slate-600 mt-0.5">
                  {c.count} shipments · cost {money(c.cost_total)} · billed {money(c.billed_total)}
                </div>
                <div className="text-[11px] text-slate-500">
                  Markup {money(c.markup_total)} ({c.effective_markup_pct != null ? `${c.effective_markup_pct}%` : "—"})
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Scanning…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="recon-empty">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2"/>
            No {bucket.replace(/_/g, " ")} drift detected.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid={`recon-table-${bucket}`}>
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tracking</th>
                <th className="text-left px-4 py-2 font-medium">Carrier</th>
                <th className="text-right px-4 py-2 font-medium">Cost</th>
                <th className="text-right px-4 py-2 font-medium">Billed</th>
                <th className="text-right px-4 py-2 font-medium">Drift</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r) => (
                <tr key={r.id} data-testid={`recon-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 mono text-[11.5px]">{r.tracking || r.id?.slice(0,8)}</td>
                  <td className="px-4 py-2">{r.carrier || "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(r.cost)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(r.billed)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                    r.drift_usd < 0 ? "text-rose-700" : "text-emerald-700"
                  }`}>
                    {r.drift_usd >= 0 ? "+" : ""}{money(r.drift_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/* ─── External trackers tab ────────────────────────────────────────── */
function ExternalTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [carrier, setCarrier] = useState("");
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("since_days", String(sinceDays));
      if (q.trim()) params.set("q", q.trim());
      if (carrier) params.set("carrier", carrier);
      const r = await api.get(`/admin/shipping/external-list?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load external trackers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, carrier]);

  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="external-kpis">
        <KpiTile label="External trackers" value={data?.kpis?.total_count ?? 0} icon={Globe} tone="slate"/>
        <KpiTile label="Unique buyers"     value={data?.kpis?.unique_buyers ?? 0} icon={Activity}/>
        <KpiTile label="Carriers seen"     value={data?.kpis?.carriers_seen ?? 0} icon={Truck}/>
        <KpiTile label={`Window`}          value={`${sinceDays}d`} icon={Clock}/>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input
            data-testid="external-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Tracking · origin · destination · note"
            className="h-9 pl-8 w-[280px]"
          />
        </div>
        <Select value={carrier || "all"} onValueChange={(v) => setCarrier(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="external-carrier">
            <SelectValue placeholder="All carriers"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All carriers</SelectItem>
            <SelectItem value="UPS">UPS</SelectItem>
            <SelectItem value="FedEx">FedEx</SelectItem>
            <SelectItem value="USPS">USPS</SelectItem>
            <SelectItem value="DHL">DHL</SelectItem>
            <SelectItem value="Unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(Number(v))}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="external-window">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 1 year</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="external-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="external-empty">
            <Globe size={32} className="mx-auto text-slate-300 mb-2"/>
            No external trackers in this window.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="external-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tracking</th>
                <th className="text-left px-4 py-2 font-medium">Carrier</th>
                <th className="text-left px-4 py-2 font-medium">Buyer</th>
                <th className="text-left px-4 py-2 font-medium">Route</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Age</th>
                <th className="text-left px-4 py-2 font-medium">Added</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`external-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 mono text-[11.5px]">{r.tracking}</td>
                  <td className="px-4 py-2">{r.carrier || "—"}</td>
                  <td className="px-4 py-2 text-[12px] text-slate-700">
                    <div className="font-medium">{r.buyer_company || "—"}</div>
                    <div className="text-[11px] text-slate-500">{r.buyer_email || "—"}</div>
                  </td>
                  <td className="px-4 py-2 text-[12px] text-slate-600 max-w-[260px] truncate">
                    {r.origin} → {r.destination}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600 font-medium">{r.days_open ?? "—"}d</td>
                  <td className="px-4 py-2 text-[12px] text-slate-500">{fmtAt(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.by_carrier && Object.keys(data.by_carrier).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="external-by-carrier">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Volume by carrier ({sinceDays}d)</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.by_carrier).map(([c, count]) => (
              <span key={c} data-testid={`external-carrier-pill-${c}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[12px]">
                <span className="font-medium text-slate-800">{c}</span>
                <span className="text-slate-500">{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── Wallet & Markup tab (Iter 213 · Shipping Wallet + Volume Tiers) ── */
function WalletTab() {
  const [subTab, setSubTab] = useState("tenants");
  const SUBS = [
    { id: "tenants", label: "Sellers",       icon: Wallet },
    { id: "margin",  label: "Margin ledger", icon: TrendingUp },
    { id: "config",  label: "Config",        icon: Settings },
  ];
  return (
    <div className="space-y-4" data-testid="wallet-tab">
      <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl p-1 w-fit">
        {SUBS.map(s => {
          const Icon = s.icon;
          const active = subTab === s.id;
          return (
            <button key={s.id} data-testid={`wallet-sub-${s.id}`} onClick={() => setSubTab(s.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}>
              <Icon size={14}/>{s.label}
            </button>
          );
        })}
      </div>
      {subTab === "tenants" && <WalletTenantsTab/>}
      {subTab === "margin"  && <WalletMarginTab/>}
      {subTab === "config"  && <WalletConfigTab/>}
    </div>
  );
}

function WalletTenantsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/shipping-wallet/tenants");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tenants");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-500">
          Every seller with a Verqo postage wallet · click a row to override rate or extend credit
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="wallet-tenants-refresh">
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="p-12 text-center text-slate-500" data-testid="wallet-tenants-empty">
            No sellers have opened a postage wallet yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="wallet-tenants-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Seller</th>
                <th className="text-right px-4 py-2 font-medium">Balance</th>
                <th className="text-right px-4 py-2 font-medium">Credit line</th>
                <th className="text-right px-4 py-2 font-medium">30d spend</th>
                <th className="text-left px-4 py-2 font-medium">Rate</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.seller_id} data-testid={`wallet-tenant-row-${r.seller_id}`}
                    onClick={() => setSelected(r)}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">{r.company || r.name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">${(r.balance_cents/100).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-indigo-700">${(r.credit_line_cents/100).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">${Number(r.spend_30d_usd).toFixed(2)}</td>
                  <td className="px-4 py-2 font-medium">{Number(r.effective_markup_pct).toFixed(1)}%</td>
                  <td className="px-4 py-2 text-[11px]">
                    <span className={`inline-block px-1.5 py-0.5 rounded ${
                      r.markup_source === "override" ? "bg-amber-100 text-amber-800" :
                      r.markup_source === "tier"     ? "bg-emerald-100 text-emerald-800" :
                      r.markup_source === "byo"      ? "bg-slate-100 text-slate-700" :
                                                        "bg-slate-100 text-slate-700"
                    }`}>{r.markup_source}</span>
                  </td>
                  <td className="px-2 py-2 text-slate-400"><ChevronRight size={14}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selected && <WalletTenantDialog seller={selected} onClose={() => setSelected(null)} onSaved={() => { load(); setSelected(null); }}/>}
    </div>
  );
}

function WalletTenantDialog({ seller, onClose, onSaved }) {
  const [override, setOverride]         = useState(seller.markup_pct_override != null ? String(seller.markup_pct_override) : "");
  const [creditLine, setCreditLine]     = useState(String((seller.credit_line_cents || 0) / 100));
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustNote, setAdjustNote]     = useState("");
  const [busy, setBusy]                 = useState(false);

  const savePatch = async () => {
    setBusy(true);
    try {
      const body = { credit_line_cents: Math.round(Number(creditLine) * 100) };
      if (override.trim() === "") body.clear_override = true;
      else body.markup_pct_override = Number(override);
      await api.patch(`/admin/shipping-wallet/tenants/${seller.seller_id}`, body);
      toast.success("Wallet settings updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const submitAdjust = async () => {
    const cents = Math.round(Number(adjustAmount) * 100);
    if (!cents) return toast.error("Enter a non-zero amount (positive to credit, negative to debit)");
    if (adjustNote.trim().length < 3) return toast.error("Note must be at least 3 characters");
    setBusy(true);
    try {
      await api.post(`/admin/shipping-wallet/tenants/${seller.seller_id}/credit-adjust`, {
        amount_cents: cents, note: adjustNote.trim(),
      });
      toast.success(`Adjusted ${cents > 0 ? "+" : ""}$${(cents/100).toFixed(2)}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Adjust failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="wallet-tenant-dialog">
        <DialogHeader>
          <DialogTitle>Shipping Wallet · {seller.company || seller.email}</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Adjust per-seller markup override, extend credit line, or manually credit/debit the wallet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px]">
            <div>Balance: <span className="font-semibold">${(seller.balance_cents/100).toFixed(2)}</span></div>
            <div>Current rate: <span className="font-semibold">{Number(seller.effective_markup_pct).toFixed(1)}%</span> ({seller.markup_source})</div>
            <div>30-day spend: <span className="font-semibold">${Number(seller.spend_30d_usd).toFixed(2)}</span></div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1">Markup override</div>
            <div className="flex items-center gap-2">
              <Input value={override} onChange={e => setOverride(e.target.value)} placeholder="e.g. 7 (leave blank for default)" className="h-9 flex-1" data-testid="wallet-override-input"/>
              <span className="text-slate-500 text-sm">%</span>
            </div>
            <div className="text-[10.5px] text-slate-500 mt-1">Blank = use volume tier / default. Applies immediately to next shipment.</div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1">Credit line</div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-sm">$</span>
              <Input value={creditLine} onChange={e => setCreditLine(e.target.value)} placeholder="0" className="h-9 flex-1" data-testid="wallet-credit-input"/>
            </div>
            <div className="text-[10.5px] text-slate-500 mt-1">Allow the seller's postage wallet to go negative by up to this amount.</div>
          </div>

          <Button onClick={savePatch} disabled={busy} data-testid="wallet-save-btn" className="w-full">
            {busy ? <Loader2 className="inline animate-spin mr-1.5" size={14}/> : null} Save settings
          </Button>

          <div className="pt-2 border-t border-slate-200">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1">Manual credit / debit</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-slate-500 text-sm">$</span>
              <Input value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="Positive to credit, negative to debit" className="h-9 flex-1" data-testid="wallet-adjust-amount"/>
            </div>
            <Textarea rows={2} value={adjustNote} onChange={e => setAdjustNote(e.target.value)} placeholder="Reason (audit log · ≥3 chars)" data-testid="wallet-adjust-note"/>
            <Button variant="outline" onClick={submitAdjust} disabled={busy} data-testid="wallet-adjust-submit" className="w-full mt-2">
              Apply adjustment
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WalletMarginTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/shipping-wallet/margin-summary?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load margin");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wallet-margin-kpis">
        <KpiTile label="Total margin"   value={data ? money(data.totals.margin_cents/100) : "—"} icon={DollarSign} tone="emerald"/>
        <KpiTile label="Carrier cost"   value={data ? money(data.totals.cost_cents/100) : "—"} icon={DollarSign}/>
        <KpiTile label="Seller billed"  value={data ? money(data.totals.billed_cents/100) : "—"} icon={DollarSign}/>
        <KpiTile label="Sellers active" value={data?.totals?.seller_count ?? "—"} icon={Activity}/>
      </div>
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="wallet-margin-window"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 1 year</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="wallet-margin-refresh">
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading…</div>
        ) : !data?.top_sellers?.length ? (
          <div className="p-12 text-center text-slate-500" data-testid="wallet-margin-empty">No margin recorded in this window.</div>
        ) : (
          <table className="w-full text-sm" data-testid="wallet-margin-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Seller</th>
                <th className="text-right px-4 py-2 font-medium">Orders</th>
                <th className="text-right px-4 py-2 font-medium">Cost</th>
                <th className="text-right px-4 py-2 font-medium">Billed</th>
                <th className="text-right px-4 py-2 font-medium">Margin</th>
              </tr>
            </thead>
            <tbody>
              {data.top_sellers.map(s => (
                <tr key={s.seller_id} data-testid={`wallet-margin-row-${s.seller_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">{s.company || s.name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{s.email}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.orders}</td>
                  <td className="px-4 py-2 text-right tabular-nums">${(s.cost_cents/100).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">${(s.billed_cents/100).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-emerald-700">${(s.margin_cents/100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function WalletConfigTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [defPct, setDefPct] = useState("");
  const [tiers, setTiers] = useState([]);
  const [laneRules, setLaneRules] = useState([]);
  const [metros, setMetros] = useState({});
  const [editingMetro, setEditingMetro] = useState(null); // metro key currently open in editor
  const [metroSearch, setMetroSearch] = useState("");
  // Lane preview widget
  const [prevOC, setPrevOC] = useState("Los Angeles");
  const [prevOS, setPrevOS] = useState("CA");
  const [prevDC, setPrevDC] = useState("Dallas");
  const [prevDS, setPrevDS] = useState("TX");
  const [prevResult, setPrevResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/shipping-wallet/config");
      setData(r.data);
      setDefPct(String(r.data.default_markup_pct));
      setTiers([...(r.data.volume_tiers || [])]);
      setLaneRules(JSON.parse(JSON.stringify(r.data.freight_lane_rules || [])));
      setMetros(r.data.metro_areas || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load config");
    }
  };
  useEffect(() => { load(); }, []);

  const updateTier = (i, field, val) => {
    const clone = [...tiers];
    clone[i] = { ...clone[i], [field]: field === "label" ? val : Number(val) };
    setTiers(clone);
  };
  const addTier = () => setTiers([...tiers, { threshold_usd: 0, markup_pct: 10, label: "New tier" }]);
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));

  // Lane rule helpers
  const updateRulePct = (ri, val) => {
    const clone = [...laneRules];
    clone[ri] = { ...clone[ri], markup_pct: Number(val) };
    setLaneRules(clone);
  };
  const updateRuleLabel = (ri, val) => {
    const clone = [...laneRules];
    clone[ri] = { ...clone[ri], label: val };
    setLaneRules(clone);
  };
  const addLane = (ri) => {
    const clone = [...laneRules];
    clone[ri] = { ...clone[ri], lanes: [...(clone[ri].lanes || []), { origin: "", destination: "" }] };
    setLaneRules(clone);
  };
  const removeLane = (ri, li) => {
    const clone = [...laneRules];
    clone[ri] = { ...clone[ri], lanes: clone[ri].lanes.filter((_, idx) => idx !== li) };
    setLaneRules(clone);
  };
  const setLaneField = (ri, li, field, val) => {
    const clone = [...laneRules];
    const lanes = [...clone[ri].lanes];
    lanes[li] = { ...lanes[li], [field]: val };
    clone[ri] = { ...clone[ri], lanes };
    setLaneRules(clone);
  };
  const addRule = () => setLaneRules([
    ...laneRules,
    { tier: (laneRules.length ? Math.max(...laneRules.map(r => r.tier)) + 1 : 1),
      markup_pct: 8, label: "New tier", lanes: [] },
  ]);
  const removeRule = (ri) => setLaneRules(laneRules.filter((_, idx) => idx !== ri));

  const previewLane = async () => {
    try {
      const r = await api.post("/admin/shipping-wallet/preview-lane", {
        origin_city: prevOC, origin_state: prevOS,
        dest_city: prevDC, dest_state: prevDS,
      });
      setPrevResult(r.data.context);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/shipping-wallet/config", {
        default_markup_pct: Number(defPct),
        volume_tiers: tiers,
        freight_lane_rules: laneRules,
        metro_areas: metros,
      });
      toast.success("Config saved · applies to next shipment");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  // Metro editor helpers
  const setMetroLabel = (key, val) => {
    setMetros({ ...metros, [key]: { ...(metros[key] || { cities: [] }), label: val } });
  };
  const addMetroCity = (key, city, state) => {
    const c = String(city || "").trim().toUpperCase();
    const s = String(state || "").trim().toUpperCase().slice(0, 2);
    if (!c || !s) return;
    const meta = metros[key] || { label: key, cities: [] };
    const exists = (meta.cities || []).some(x => (x[0] || "").toUpperCase() === c && (x[1] || "").toUpperCase() === s);
    if (exists) { toast.info(`${c}, ${s} already in ${meta.label || key}`); return; }
    setMetros({ ...metros, [key]: { ...meta, cities: [...(meta.cities || []), [c, s]] } });
  };
  const removeMetroCity = (key, i) => {
    const meta = metros[key];
    if (!meta) return;
    setMetros({ ...metros, [key]: { ...meta, cities: meta.cities.filter((_, idx) => idx !== i) } });
  };
  const addNewMetro = () => {
    const key = prompt("Metro key (lowercase snake_case, e.g. 'san_diego'):");
    if (!key) return;
    const norm = key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!norm) return;
    if (metros[norm]) { toast.error("Metro key already exists"); return; }
    const label = prompt("Metro label (display name, e.g. 'San Diego metro'):", norm.replace(/_/g, " "));
    setMetros({ ...metros, [norm]: { label: label || norm, cities: [] } });
    setEditingMetro(norm);
  };
  const removeMetro = (key) => {
    if (!window.confirm(`Delete metro "${metros[key]?.label || key}"? Any lane rules using it must be updated.`)) return;
    const clone = { ...metros };
    delete clone[key];
    setMetros(clone);
    if (editingMetro === key) setEditingMetro(null);
  };

  const metroKeys = Object.keys(metros || {}).sort();

  if (!data) return <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading config…</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1">Default markup (all sellers, all modes)</div>
        <div className="flex items-center gap-2">
          <Input value={defPct} onChange={e => setDefPct(e.target.value)} type="number" step="0.1" className="h-9 w-32" data-testid="config-default-pct"/>
          <span className="text-slate-500 text-sm">%</span>
        </div>
        <div className="text-[10.5px] text-slate-500 mt-2">
          Applied when no lane rule, volume tier, or per-seller override matches. Order of precedence: admin per-seller override &rarr; freight lane rule &rarr; volume tier &rarr; default. Every Verqo-brokered label gets the 10% markup — no BYO carrier exceptions.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Volume tiers (all modes)</div>
          <Button variant="outline" size="sm" onClick={addTier} data-testid="config-tier-add">+ Add tier</Button>
        </div>
        <div className="space-y-2">
          {tiers.map((t, i) => (
            <div key={i} data-testid={`config-tier-row-${i}`} className="grid grid-cols-[1fr,120px,120px,auto] items-center gap-2">
              <Input value={t.label} onChange={e => updateTier(i, "label", e.target.value)} placeholder="Tier label" className="h-9 text-sm" data-testid={`config-tier-label-${i}`}/>
              <Input value={t.threshold_usd} onChange={e => updateTier(i, "threshold_usd", e.target.value)} type="number" step="100" className="h-9 text-sm" data-testid={`config-tier-threshold-${i}`}/>
              <div className="flex items-center gap-1">
                <Input value={t.markup_pct} onChange={e => updateTier(i, "markup_pct", e.target.value)} type="number" step="0.1" className="h-9 text-sm" data-testid={`config-tier-pct-${i}`}/>
                <span className="text-slate-500 text-sm">%</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeTier(i)} data-testid={`config-tier-remove-${i}`}>
                <XCircle size={14} className="text-rose-500"/>
              </Button>
            </div>
          ))}
        </div>
        <div className="text-[10.5px] text-slate-500 mt-3">
          Sellers automatically move to the highest tier they qualify for based on 30-day rolling shipping spend.
        </div>
      </div>

      {/* Freight lane rules */}
      <div className="bg-white border border-indigo-200 rounded-xl p-4" data-testid="config-lane-rules">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-medium">Freight lane rules</div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">
              Origin &rarr; destination overrides on select lanes. Freight-only. Sorted by tier (lower # wins).
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={addRule} data-testid="config-lane-rule-add">+ Add tier</Button>
        </div>

        <div className="space-y-4">
          {laneRules.map((rule, ri) => (
            <div key={ri} className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" data-testid={`config-lane-rule-${ri}`}>
              <div className="grid grid-cols-[80px,1fr,120px,auto] items-center gap-2 mb-2">
                <div className="text-xs font-semibold text-indigo-800">Tier {rule.tier}</div>
                <Input value={rule.label} onChange={e => updateRuleLabel(ri, e.target.value)} placeholder="Rule label" className="h-8 text-sm" data-testid={`config-lane-rule-label-${ri}`}/>
                <div className="flex items-center gap-1">
                  <Input value={rule.markup_pct} onChange={e => updateRulePct(ri, e.target.value)} type="number" step="0.1" className="h-8 text-sm" data-testid={`config-lane-rule-pct-${ri}`}/>
                  <span className="text-slate-500 text-sm">%</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeRule(ri)} data-testid={`config-lane-rule-remove-${ri}`}>
                  <XCircle size={14} className="text-rose-500"/>
                </Button>
              </div>

              <div className="space-y-1.5">
                {(rule.lanes || []).map((lane, li) => (
                  <div key={li} className="grid grid-cols-[1fr,20px,1fr,auto] items-center gap-2" data-testid={`config-lane-row-${ri}-${li}`}>
                    <Select value={lane.origin} onValueChange={v => setLaneField(ri, li, "origin", v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid={`config-lane-origin-${ri}-${li}`}>
                        <SelectValue placeholder="Origin metro"/>
                      </SelectTrigger>
                      <SelectContent>
                        {metroKeys.map(k => (
                          <SelectItem key={k} value={k}>{metros[k].label || k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-slate-400 text-center">→</div>
                    <Select value={lane.destination} onValueChange={v => setLaneField(ri, li, "destination", v)}>
                      <SelectTrigger className="h-8 text-sm" data-testid={`config-lane-dest-${ri}-${li}`}>
                        <SelectValue placeholder="Destination metro"/>
                      </SelectTrigger>
                      <SelectContent>
                        {metroKeys.map(k => (
                          <SelectItem key={k} value={k}>{metros[k].label || k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => removeLane(ri, li)} data-testid={`config-lane-remove-${ri}-${li}`}>
                      <XCircle size={14} className="text-rose-500"/>
                    </Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => addLane(ri)} className="text-xs" data-testid={`config-lane-add-${ri}`}>
                  + Add lane
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metro areas editor (admin-only) */}
      <MetroAreasEditor
        metros={metros}
        editingMetro={editingMetro}
        setEditingMetro={setEditingMetro}
        metroSearch={metroSearch}
        setMetroSearch={setMetroSearch}
        setMetroLabel={setMetroLabel}
        addMetroCity={addMetroCity}
        removeMetroCity={removeMetroCity}
        addNewMetro={addNewMetro}
        removeMetro={removeMetro}
      />

      {/* Preview widget */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="config-lane-preview">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-3">
          Preview a lane
        </div>
        <div className="grid grid-cols-[1fr,60px,1fr,60px,auto] items-end gap-2">
          <div>
            <div className="text-[10.5px] text-slate-500 mb-0.5">Origin city</div>
            <Input value={prevOC} onChange={e => setPrevOC(e.target.value)} className="h-8 text-sm" data-testid="preview-origin-city"/>
          </div>
          <div>
            <div className="text-[10.5px] text-slate-500 mb-0.5">State</div>
            <Input value={prevOS} onChange={e => setPrevOS(e.target.value)} maxLength={2} className="h-8 text-sm uppercase" data-testid="preview-origin-state"/>
          </div>
          <div>
            <div className="text-[10.5px] text-slate-500 mb-0.5">Destination city</div>
            <Input value={prevDC} onChange={e => setPrevDC(e.target.value)} className="h-8 text-sm" data-testid="preview-dest-city"/>
          </div>
          <div>
            <div className="text-[10.5px] text-slate-500 mb-0.5">State</div>
            <Input value={prevDS} onChange={e => setPrevDS(e.target.value)} maxLength={2} className="h-8 text-sm uppercase" data-testid="preview-dest-state"/>
          </div>
          <Button variant="outline" size="sm" onClick={previewLane} data-testid="preview-lane-btn">
            Preview
          </Button>
        </div>
        {prevResult && (
          <div className="mt-3 rounded-lg bg-indigo-50/60 border border-indigo-200 p-3" data-testid="preview-lane-result">
            <div className="text-sm">
              <span className="text-2xl font-bold text-indigo-900 tabular-nums" data-testid="preview-lane-pct">
                {Number(prevResult.pct).toFixed(1)}%
              </span>
              <span className="text-slate-500 text-[11px] ml-2 uppercase tracking-wide">
                source: <span data-testid="preview-lane-source" className="font-mono">{prevResult.source}</span>
              </span>
            </div>
            <div className="text-[11.5px] text-slate-700 mt-1">{prevResult.reason}</div>
          </div>
        )}
      </div>

      <Button onClick={save} disabled={busy} data-testid="config-save-btn" className="w-full">
        {busy ? <Loader2 className="animate-spin inline mr-1.5" size={14}/> : null} Save config
      </Button>
    </div>
  );
}


function MetroAreasEditor({
  metros, editingMetro, setEditingMetro, metroSearch, setMetroSearch,
  setMetroLabel, addMetroCity, removeMetroCity, addNewMetro, removeMetro,
}) {
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("");
  const keys = Object.keys(metros || {}).sort();
  const filteredKeys = keys.filter(k => {
    if (!metroSearch) return true;
    const q = metroSearch.toLowerCase();
    const label = (metros[k]?.label || "").toLowerCase();
    if (k.includes(q) || label.includes(q)) return true;
    return (metros[k]?.cities || []).some(c => (c[0] || "").toLowerCase().includes(q));
  });
  const activeMeta = editingMetro ? metros[editingMetro] : null;

  const submitNewCity = (e) => {
    if (e) e.preventDefault();
    if (!editingMetro || !newCity.trim() || !newState.trim()) return;
    addMetroCity(editingMetro, newCity, newState);
    setNewCity(""); setNewState("");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="config-metros-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Metro areas</div>
          <div className="text-[10.5px] text-slate-500 mt-0.5">
            Fine-tune which cities count as each metro for lane matching. Additive edits — deleting a city here disables lane matches for shipments FROM that city.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={addNewMetro} data-testid="config-metro-add-new">
          + Add metro
        </Button>
      </div>

      <div className="grid grid-cols-[280px,1fr] gap-3">
        {/* Metro list */}
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50/40">
          <div className="p-2 border-b border-slate-200 bg-white">
            <Input
              placeholder="Search metro or city…"
              value={metroSearch}
              onChange={e => setMetroSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="config-metro-search"
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {filteredKeys.length === 0 && (
              <div className="p-4 text-center text-slate-400 text-xs">No metros match</div>
            )}
            {filteredKeys.map(k => {
              const m = metros[k] || {};
              const active = editingMetro === k;
              return (
                <button
                  key={k}
                  onClick={() => setEditingMetro(k)}
                  data-testid={`config-metro-item-${k}`}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-white text-sm ${
                    active ? "bg-indigo-50 border-l-2 border-l-indigo-500" : ""
                  }`}
                >
                  <div className="font-medium text-slate-800 truncate">{m.label || k}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    <span className="mono">{k}</span> · {(m.cities || []).length} cities
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Metro editor panel */}
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
          {!editingMetro ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm py-12">
              Select a metro on the left to edit its cities.
            </div>
          ) : !activeMeta ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm py-12">Not found.</div>
          ) : (
            <div className="space-y-3" data-testid={`config-metro-editing-${editingMetro}`}>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium mb-1">
                    Metro key <span className="text-slate-400 normal-case">(immutable)</span>
                  </div>
                  <div className="mono text-sm text-slate-700 bg-white border border-slate-200 rounded px-2 py-1.5" data-testid="config-metro-key">
                    {editingMetro}
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => removeMetro(editingMetro)}
                  data-testid={`config-metro-remove-${editingMetro}`}
                >
                  <XCircle size={14} className="text-rose-500 mr-1"/> Delete metro
                </Button>
              </div>

              <div>
                <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium mb-1">Display label</div>
                <Input
                  value={activeMeta.label || ""}
                  onChange={e => setMetroLabel(editingMetro, e.target.value)}
                  className="h-8 text-sm"
                  data-testid="config-metro-label"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium">
                    Cities ({(activeMeta.cities || []).length})
                  </div>
                </div>
                <form onSubmit={submitNewCity} className="flex items-center gap-2 mb-2">
                  <Input
                    placeholder="City name"
                    value={newCity}
                    onChange={e => setNewCity(e.target.value)}
                    className="h-8 text-sm flex-1"
                    data-testid="config-metro-add-city"
                  />
                  <Input
                    placeholder="ST"
                    value={newState}
                    onChange={e => setNewState(e.target.value.toUpperCase().slice(0, 2))}
                    className="h-8 text-sm w-16 uppercase"
                    maxLength={2}
                    data-testid="config-metro-add-state"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    size="sm"
                    disabled={!newCity.trim() || !newState.trim()}
                    data-testid="config-metro-add-city-btn"
                  >
                    Add
                  </Button>
                </form>
                <div className="flex flex-wrap gap-1.5 max-h-[280px] overflow-y-auto" data-testid="config-metro-city-list">
                  {(activeMeta.cities || []).map((c, i) => (
                    <div
                      key={`${c[0]}-${c[1]}-${i}`}
                      className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs"
                      data-testid={`config-metro-city-${i}`}
                    >
                      <span className="font-medium text-slate-700">{c[0]}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{c[1]}</span>
                      <button
                        type="button"
                        onClick={() => removeMetroCity(editingMetro, i)}
                        className="ml-0.5 text-slate-400 hover:text-rose-500"
                        data-testid={`config-metro-city-remove-${i}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(activeMeta.cities || []).length === 0 && (
                    <div className="text-slate-400 text-xs py-2">No cities yet. Add one above.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─── Small components ─────────────────────────────────────────────── */
function KpiTile({ label, value, icon: Icon, tone = "slate" }) {
  const TONES = {
    slate:   "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${TONES[tone] || TONES.slate}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-70">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className="text-xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function FlagTile({ label, value, active, onClick, icon: Icon, tone, testid }) {
  const TONES = {
    amber:   active ? "bg-amber-100 border-amber-400" : "bg-amber-50 border-amber-200 hover:border-amber-300",
    rose:    active ? "bg-rose-100 border-rose-400" : "bg-rose-50 border-rose-200 hover:border-rose-300",
    emerald: active ? "bg-emerald-100 border-emerald-400" : "bg-emerald-50 border-emerald-200 hover:border-emerald-300",
    violet:  active ? "bg-violet-100 border-violet-400" : "bg-violet-50 border-violet-200 hover:border-violet-300",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-xl border-2 p-3 text-left transition-colors ${TONES[tone] || TONES.amber}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-70">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className="text-xl font-bold tabular-nums mt-1">{value}</div>
    </button>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-[13px] font-medium text-slate-800 break-all">{String(value || "—")}</div>
    </div>
  );
}
